import { useTranslation } from 'react-i18next';
import { UI_COLORS } from '../../utils/colors';
import PortalModal, { InfoRow } from './PortalModal';
import { formatDate, formatMoney } from '../portalUtils';

/**
 * Detail for a customer SALE (what they sell to the company). PO-shaped: item
 * quantity unit is `unit` (singular). counterparty_company is the company ("To").
 * There is no meaningful role to show (viewer is always the supplier).
 */
const PortalSaleViewModal = ({ isOpen, onClose, order, loading, statusLabels = {} }) => {
  const { t } = useTranslation();
  const s = t('portal.sales', { returnObjects: true });
  const c = t('portal.common', { returnObjects: true });
  const items = Array.isArray(order?.items) ? order.items : [];
  const statusText = order?.status ? (statusLabels[order.status] || order.status) : '';

  return (
    <PortalModal
      isOpen={isOpen}
      onClose={onClose}
      loading={loading}
      title={`${s?.detailTitle || 'Sales Order'} ${order?.po_number || ''}`.trim()}
      maxWidth="max-w-4xl"
    >
      {order && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <InfoRow label={s?.columns?.date || 'Date'} value={formatDate(order.date)} />
            <InfoRow label={s?.columns?.status || 'Status'} value={statusText} />
            <InfoRow label={s?.columns?.counterparty || 'To'} value={order.counterparty_company || '—'} />
            <InfoRow label={s?.columns?.expectedDelivery || 'Expected Delivery'} value={formatDate(order.expected_delivery_date)} />
            <InfoRow label={s?.columns?.total || 'Total'} value={formatMoney(order.total_amount)} />
          </div>

          <div>
            <h3 className="text-sm font-bold mb-2" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{c?.items || 'Items'}</h3>
            <div className="overflow-x-auto rounded-lg border" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ backgroundColor: UI_COLORS.BACKGROUNDS.SECTION }}>
                    <th className="text-left px-3 py-2 font-semibold">{c?.product || 'Product'}</th>
                    <th className="text-left px-3 py-2 font-semibold">{c?.size || 'Size'}</th>
                    <th className="text-right px-3 py-2 font-semibold">{c?.quantity || 'Qty'}</th>
                    <th className="text-right px-3 py-2 font-semibold">{c?.price || 'Price'}</th>
                    <th className="text-right px-3 py-2 font-semibold">{c?.amount || 'Amount'}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr><td colSpan={5} className="px-3 py-4 text-center" style={{ color: UI_COLORS.TEXT.MUTED }}>{c?.noItems || 'No items'}</td></tr>
                  ) : items.map((it, i) => (
                    <tr key={i} className="border-t" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                      <td className="px-3 py-2">{it.product_name || '—'}</td>
                      <td className="px-3 py-2">{it.size_name || '—'}</td>
                      <td className="px-3 py-2 text-right">{it.quantity} {it.unit || it.units || ''}</td>
                      <td className="px-3 py-2 text-right">{formatMoney(it.price)}</td>
                      <td className="px-3 py-2 text-right">{formatMoney(it.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </PortalModal>
  );
};

export default PortalSaleViewModal;
